/**
 * Axios singleton for the OpenSoi admin API.
 *
 * Every request:
 *   - Sends `Authorization: Bearer <fresh Firebase ID token>`.
 *   - Sends `Accept-Language: en` (the admin UI is English-only for v1).
 *   - 30s timeout, baseURL from VITE_API_BASE_URL.
 *
 * On 401 we attempt a single token refresh + retry (mirrors the mobile
 * app's `services/api/client.ts` flow). If the refresh also fails we
 * sign out so the user lands back on the sign-in screen instead of
 * staring at a stale list.
 */
import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { getFirebaseAuth, getIdToken, signOut } from './firebase';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://api.opensoi.app';

export interface ApiErrorPayload {
  /** Bilingual error from the backend (`httpx.APIError`). */
  readonly message_en?: string;
  readonly message_th?: string;
  readonly error?: string;
  readonly code?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly payload: ApiErrorPayload | null;

  constructor(message: string, status: number, payload: ApiErrorPayload | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Accept-Language': 'en' },
});

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // Attach a fresh ID token. The Firebase SDK caches tokens for 1h and
  // refreshes them automatically when expired, so non-forced calls are
  // cheap. We deliberately fetch on every request rather than caching —
  // an admin pane sitting open for hours otherwise starts attaching a
  // stale token after the first hour and wastes a 401 + retry round-trip.
  const auth = getFirebaseAuth();
  if (auth.currentUser !== null) {
    const token = await auth.currentUser.getIdToken(false);
    const headers = AxiosHeaders.from(config.headers);
    headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
  }
  return config;
});

// Track in-flight refresh so concurrent 401s coalesce to one popup.
let inFlightRefresh: Promise<string | null> | null = null;
async function refreshOnce(): Promise<string | null> {
  if (inFlightRefresh !== null) return inFlightRefresh;
  inFlightRefresh = getIdToken(true).finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

apiClient.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError<ApiErrorPayload>) => {
    const status = error.response?.status ?? 0;
    const payload = error.response?.data ?? null;

    // 401 — try a single forced token refresh + retry. If the refresh
    // returns null (no signed-in user) or the retry still 401s, sign
    // out and surface the error.
    if (status === 401 && error.config !== undefined) {
      const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
      if (original._retried !== true) {
        original._retried = true;
        const fresh = await refreshOnce();
        if (fresh !== null) {
          const headers = AxiosHeaders.from(original.headers);
          headers.set('Authorization', `Bearer ${fresh}`);
          original.headers = headers;
          return apiClient.request(original);
        }
      }
      // Refresh failed (or already retried). Drop the session so the
      // user lands on the sign-in screen instead of an opaque error.
      try {
        await signOut();
      } catch {
        // best-effort; no rollback path matters here
      }
    }

    const enMsg = payload?.message_en ?? payload?.error ?? error.message;
    throw new ApiError(enMsg, status, payload);
  },
);

// ─── Typed bindings for /v1/admin/* ─────────────────────────────────

export interface AdminLocation {
  readonly lat: number;
  readonly lng: number;
}

export interface AdminSubmission {
  readonly id: string;
  readonly name_th: string;
  readonly name_en?: string | null;
  readonly category?: string | null;
  readonly dishes: readonly string[];
  readonly location: AdminLocation;
  readonly address?: string | null;
  readonly posted_hours: Record<string, unknown>;
  readonly photo_media_key?: string | null;
  readonly photo_url: string;
  readonly submitter_email: string;
  readonly submitted_at: string;
  readonly suspicious_flags: readonly string[];
}

export interface ListSubmissionsResponse {
  readonly submissions: readonly AdminSubmission[];
  readonly next_cursor?: string;
}

/**
 * GET /v1/admin/submissions?status=pending&limit=50[&cursor=...]
 *
 * The backend currently only honours `status=pending`; the param is
 * sent anyway so a future backend that returns approved/rejected lists
 * Just Works with no client-side change.
 */
export async function listSubmissions(
  params: { status?: 'pending' | 'approved' | 'rejected'; limit?: number; cursor?: string } = {},
): Promise<ListSubmissionsResponse> {
  const { status = 'pending', limit = 50, cursor } = params;
  const search = new URLSearchParams({ status, limit: String(limit) });
  if (cursor !== undefined && cursor.length > 0) search.set('cursor', cursor);
  const res = await apiClient.get<ListSubmissionsResponse>(
    `/v1/admin/submissions?${search.toString()}`,
  );
  return res.data;
}

/** POST /v1/admin/submissions/:id/approve */
export async function approveSubmission(id: string): Promise<void> {
  await apiClient.post(`/v1/admin/submissions/${encodeURIComponent(id)}/approve`);
}

/**
 * POST /v1/admin/submissions/:id/reject
 *
 * Backend requires BOTH reasons (English + Thai) — the FCM payload to
 * the submitter pulls the language matching their preference. The UI
 * collects both via the reject modal.
 */
export async function rejectSubmission(
  id: string,
  reason: { en: string; th: string },
): Promise<void> {
  await apiClient.post(`/v1/admin/submissions/${encodeURIComponent(id)}/reject`, {
    reason_en: reason.en,
    reason_th: reason.th,
  });
}
